import { Box, Typography } from '@mui/material';
import { type GridApiPro, useGridApiRef } from '@mui/x-data-grid-pro';
import type { GridApiCommunity } from '@mui/x-data-grid/internals';
import ExcelJS from 'exceljs';
import { type MutableRefObject, useCallback, useMemo, useState } from 'react';
import { notDefined, whenDefined } from '@vy/null';
import { now, vyDate } from '@vy/utils';
import type { FilterLineType } from '../../../../components/new-design/vyTableFilter';
import { useFiltersParam } from '../../../../hooks/search-params/use-filters-param';
import { useGridColumnSettings } from '../../../../hooks/use-grid-column-settings';
import {
  useRefreshUsageReportV2Report,
  useUsageReportV2Report,
} from '../../../../hooks/requests/use-usage-report-v2-report';
import { useSearchWithSearchParams } from '../../../../hooks/use-search-with-search-params';
import { toastFromPromise } from '../../../../utils/vy-toasts';
import { formatDate } from '../../../../utils/date';
import { REPORT_TYPES } from '../../reports-page/reports-page-types/report-type';
import { buildUsageReportV2Export } from '../../reports-page/reports-page-utils/usage-report-v2-report-utils/build-usage-report-v2-export';
import { ReportTable } from '../../reports-components/ReportTable';
import { useFilteredUsageReportV2 } from '../hooks/use-filtered-usage-report-v2';
import { useUsageReportV2FilterFields } from '../hooks/use-usage-report-v2-filter-fields';
import { UsageReportV2ReportTable } from './UsageReportV2ReportTable/UsageReportV2ReportTable';
import { buildUsageReportV2Columns } from './UsageReportV2ReportTable/UsageReportV2ReportTableColumns';
import { UsageReportV2ReportToolbar } from './UsageReportV2ReportToolbar';

const FILTERS_PARAM_NAME = 'usageReportV2Filters';

export const UsageReportV2ReportContent: React.FC = () => {
  const apiRef = useGridApiRef();

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [searchText, setSearchText] = useSearchWithSearchParams();
  const [fromDate, setFromDate] = useState<Date | null>(() =>
    vyDate().subtract(11, 'month').startOf('month').toDate(),
  );
  const [toDate, setToDate] = useState<Date | null>(() =>
    vyDate().startOf('month').toDate(),
  );

  const period = useMemo(
    () => ({ startTime: fromDate ?? undefined, endTime: toDate ?? undefined }),
    [fromDate, toDate],
  );

  const { data: usageReportV2 } = useUsageReportV2Report(period);
  const refreshReport = useRefreshUsageReportV2Report(period);

  const filterFields = useUsageReportV2FilterFields(usageReportV2?.reportItems ?? []);
  const [rawFilters, setRawFilters] = useFiltersParam<FilterLineType>(FILTERS_PARAM_NAME, filterFields);
  const filters = useMemo(() => rawFilters ?? [], [rawFilters]);

  const handleFiltersChange = useCallback(
    (nextFilters: FilterLineType[]) => setRawFilters(nextFilters),
    [setRawFilters],
  );

  const filteredReport = useFilteredUsageReportV2(usageReportV2, filters, searchText);

  const columns = useMemo(
    () =>
      buildUsageReportV2Columns(
        usageReportV2?.customerCustomFieldNames ?? [],
        usageReportV2?.contractCustomFieldNames ?? [],
      ),
    [usageReportV2?.customerCustomFieldNames, usageReportV2?.contractCustomFieldNames],
  );

  const {
    isSettingsOpen,
    openSettings,
    closeSettings,
    columnSettings,
    handleColumnVisibilityChange,
  } = useGridColumnSettings(apiRef as MutableRefObject<GridApiCommunity>, columns);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await toastFromPromise(refreshReport(), {
      pending: 'Refreshing report...',
      success: 'Report refreshed',
      error: 'Failed to refresh report',
    }).finally(() => setIsRefreshing(false));
  };

  const handleExportToXlsx = async () => {
    if (notDefined(filteredReport)) {
      return undefined;
    }

    const fileName = `Usage Report v2 (${formatDate(now(), 'DD.MM.YYYY')}).xlsx`;
    const visibleFields = new Set(
      apiRef.current?.getVisibleColumns().map((column) => column.field) ?? [],
    );
    const hiddenFields = new Set(
      columns.map((column) => column.field).filter((field) => !visibleFields.has(field)),
    );

    await toastFromPromise(
      (async () => {
        const workbook = new ExcelJS.Workbook();
        buildUsageReportV2Export(workbook, filteredReport, hiddenFields);
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = fileName;
        anchor.click();
        URL.revokeObjectURL(url);
      })(),
      {
        pending: 'Downloading...',
        success: 'Download complete',
        error: 'Failed to download',
      },
    );
  };

  const reportGeneratedAtDate = whenDefined(usageReportV2?.reportGeneratedAt, vyDate, null);

  const lastRefreshedLabel = reportGeneratedAtDate ? (
    <Typography variant="caption" sx={{
      color: "text.secondary"
    }}>
      Last updated: {reportGeneratedAtDate.format('MMM D, YYYY [at] h:mm A')}
    </Typography>
  ) : null;

  return (
    <>
      <Box
        sx={{
          width: '100%',
          height: 30,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          mb: 1,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 500, fontSize: '20px' }}>
          Usage Report
        </Typography>
        {lastRefreshedLabel}
      </Box>

      <ReportTable
        title="Usage Report"
        hideTitle
        exportCsvFileName="Usage Report"
        reportCreationDate={usageReportV2?.reportGeneratedAt ?? now()}
        reportType={REPORT_TYPES.USAGE_REPORT_V2}
        apiRef={apiRef as MutableRefObject<GridApiCommunity>}
        onExportCsv={handleExportToXlsx}
        hideMenu
      >
        <UsageReportV2ReportToolbar
          onSearchChange={(value) => setSearchText(value)}
          searchDefaultValue={searchText ?? ''}
          fromDate={fromDate}
          onFromDateChange={setFromDate}
          toDate={toDate}
          onToDateChange={setToDate}
          filterFields={filterFields}
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onExport={handleExportToXlsx}
          onRefresh={handleRefresh}
          isRefreshing={isRefreshing}
          isColumnSettingsOpen={isSettingsOpen}
          onOpenColumnSettings={openSettings}
          onCloseColumnSettings={closeSettings}
          columnSettings={columnSettings}
          onColumnVisibilityChange={handleColumnVisibilityChange}
        />

        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <UsageReportV2ReportTable
            period={period}
            searchText={searchText}
            filters={filters}
            columns={columns}
            apiRef={apiRef as MutableRefObject<GridApiPro>}
          />
        </Box>
      </ReportTable>
    </>
  );
};
